import React, { useState, useEffect, useContext, useCallback } from "react";
import { AppSettings } from "../../config/app-settings.js";
import Preloader from "../../components/custom/preLoader.js";
import axiosInstance, { CheckUserExpiry, getUserDetails } from "../../components/content/axiosConfig";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { Icon } from "@iconify/react";
import showErrorToast, { showSubscriptionSuccessToast } from "../../components/toast/toast";
import StripePaymentSection from "../../components/custom/StripePaymentSection.jsx";
import { normalizeCountryCode } from "../../utils/countries";
import { checkoutPageStyles } from "../../components/custom/checkoutPageStyles.js";

function parseFeatures(features) {
  if (!features) return [];
  if (Array.isArray(features)) return features;
  try {
    const parsed = JSON.parse(features);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function formatDurationLabel(duration) {
  if (duration === 12) return "/ year";
  return `/ ${duration} month${duration === 1 ? "" : "s"}`;
}

const Payment = () => {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const context = useContext(AppSettings);
  const [loading, setLoading] = useState(false);
  const [pkg, setPackage] = useState({});
  const [clientSecret, setClientSecret] = useState("");
  const [clientSecretType, setClientSecretType] = useState("payment");
  const [confirmedTotal, setConfirmedTotal] = useState(null);
  const [licensedPondCount, setLicensedPondCount] = useState(1);
  const [subscriptionId, setSubscriptionId] = useState("");
  const [recurringLabel, setRecurringLabel] = useState("");
  const [dueToday, setDueToday] = useState(null);
  const [renewalAmount, setRenewalAmount] = useState(null);
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [country, setCountry] = useState("US");
  const [couponCode, setCouponCode] = useState("");
  const [appliedCouponCode, setAppliedCouponCode] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponMessage, setCouponMessage] = useState("");
  const [couponError, setCouponError] = useState(false);
  const [originalTotal, setOriginalTotal] = useState(null);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountedTotal, setDiscountedTotal] = useState(null);

  const pondCount = Math.max(1, parseInt(new URLSearchParams(location.search).get("ponds") || "1", 10));

  const CheckExpiry = useCallback(async () => {
    await CheckUserExpiry();
  }, []);

  const initializeCheckout = useCallback(async (couponToApply = "") => {
    const paymentIntentResponse = await axiosInstance.post(`api/create-payment-intent`, {
      type: "package",
      packageId: parseInt(id, 10),
      pondCount,
      country: normalizeCountryCode(country),
      couponCode: couponToApply || undefined,
    });

    const paymentIntentData = paymentIntentResponse.data;
    if (!paymentIntentData.clientSecret) {
      throw new Error("Failed to retrieve clientSecret from server.");
    }
    setClientSecret(paymentIntentData.clientSecret);
    setClientSecretType(paymentIntentData.clientSecretType || "payment");
    setConfirmedTotal(paymentIntentData.amount);
    setOriginalTotal(paymentIntentData.originalTotal ?? paymentIntentData.amount);
    setDiscountAmount(paymentIntentData.discountAmount ?? 0);
    setDiscountedTotal(paymentIntentData.discountedTotal ?? paymentIntentData.amount);
    setLicensedPondCount(paymentIntentData.licensedPondCount ?? pondCount);
    setSubscriptionId(paymentIntentData.subscriptionId || "");
    setRecurringLabel(paymentIntentData.recurringLabel || "");
    setDueToday(paymentIntentData.dueToday ?? paymentIntentData.amount ?? null);
    setRenewalAmount(paymentIntentData.renewalAmount ?? paymentIntentData.amount ?? null);
    setCustomerName(paymentIntentData.customerName || getUserDetails().FullName || "");
    setCustomerEmail(paymentIntentData.customerEmail || getUserDetails().Email || "");
    if (paymentIntentData.customerCountry) {
      setCountry(paymentIntentData.customerCountry);
    }
    return paymentIntentData;
  }, [id, pondCount, country]);

  const fetchPackageData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`api/Packages/GetPackage/${id}`);
      const packageData = response.data;
      setPackage(packageData);
      setAppliedCouponCode("");
      setCouponMessage("");
      setCouponError(false);
      await initializeCheckout("");
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || "Failed to load payment details";
      setError(`Failed to load payment details: ${errorMessage}`);
      showErrorToast(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [id, initializeCheckout]);

  const handleApplyCoupon = async () => {
    const code = couponCode.trim();
    if (!code) return;

    setCouponLoading(true);
    setCouponMessage("");
    setCouponError(false);
    setError(null);

    try {
      const validateRes = await axiosInstance.post("api/Coupons/Validate", {
        code,
        packageId: parseInt(id, 10),
        pondCount,
      });

      if (!validateRes.data?.valid) {
        setCouponError(true);
        setCouponMessage(validateRes.data?.message || "Invalid coupon code");
        setAppliedCouponCode("");
        return;
      }

      await initializeCheckout(code);
      setAppliedCouponCode(code);
      setCouponMessage(validateRes.data.message || "Coupon applied successfully");
      setCouponError(false);
    } catch (err) {
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        "Could not apply coupon";
      setCouponError(true);
      setCouponMessage(errorMessage);
      showErrorToast(errorMessage);
    } finally {
      setCouponLoading(false);
    }
  };

  useEffect(() => {
    context.handleSetAppSidebarNone(true);
    context.handleSetAppHeaderNone(false);
    context.handleSetAppContentClass("p-0");

    return () => {
      context.handleSetAppSidebarNone(false);
      context.handleSetAppHeaderNone(false);
      context.handleSetAppContentClass("");
    };
  }, [context]);

  useEffect(() => {
    fetchPackageData();
  }, [fetchPackageData]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements || !clientSecret) {
      const errorMessage = "Payment system is not ready. Please try again.";
      setError(errorMessage);
      showErrorToast(errorMessage);
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      const errorMessage = "Payment form is not loaded. Please try again.";
      setError(errorMessage);
      showErrorToast(errorMessage);
      return;
    }

    setLoading(true);
    try {
      const user = getUserDetails();
      const billingName = customerName || user.FullName || "PondStat Customer";
      const billingEmail = customerEmail || user.Email || "";
      const billingCountry = normalizeCountryCode(country);
      const billingDetails = {
        name: billingName,
        email: billingEmail || undefined,
        address: { country: billingCountry },
      };
      const isSetupCheckout = clientSecretType === "setup" || dueToday === 0;
      let confirmedIntentId = null;

      if (isSetupCheckout) {
        const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
          payment_method: {
            card: cardElement,
            billing_details: billingDetails,
          },
        });

        if (stripeError) {
          const errorMessage = `Payment failed: ${stripeError.message}`;
          setError(errorMessage);
          showErrorToast(errorMessage);
          return;
        }

        if (setupIntent.status !== "succeeded") {
          const errorMessage = "Payment method was not confirmed. Please try again.";
          setError(errorMessage);
          showErrorToast(errorMessage);
          return;
        }

        confirmedIntentId = setupIntent.id;
      } else {
        const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: cardElement,
            billing_details: billingDetails,
          },
          receipt_email: billingEmail || undefined,
        });

        if (stripeError) {
          const errorMessage = `Payment failed: ${stripeError.message}`;
          setError(errorMessage);
          showErrorToast(errorMessage);
          return;
        }

        if (paymentIntent.status !== "succeeded") {
          const errorMessage = "Payment was not completed. Please try again.";
          setError(errorMessage);
          showErrorToast(errorMessage);
          return;
        }

        confirmedIntentId = paymentIntent.id;
      }

      if (confirmedIntentId) {
        const amountPaid =
          dueToday === 0 ? 0 : discountedTotal ?? confirmedTotal ?? pkg.Amount;

        const purchaseResponse = await axiosInstance.post(`api/fisher/PurchasePackage`, {
          PackageID: parseInt(id, 10),
          LicensedPondCount: licensedPondCount,
          AmountPaid: amountPaid,
          StripePaymentIntentId: confirmedIntentId,
          StripeSubscriptionId: subscriptionId,
          Country: billingCountry,
        });

        await CheckExpiry();
        localStorage.setItem("isExpired", "false");
        const notification =
          purchaseResponse.data?.notification ||
          purchaseResponse.data?.message ||
          "Subscription activated successfully.";
        showSubscriptionSuccessToast(notification);
        history.push("/");
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "An error occurred during payment";
      setError(`An error occurred: ${errorMessage}`);
      showErrorToast(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!stripe || !elements) {
    return <Preloader />;
  }

  const featureList = parseFeatures(pkg.Features);
  const displayTotal = confirmedTotal ?? pkg.Amount;
  const amountDue = dueToday ?? (discountedTotal ?? displayTotal);
  const packagesPath = localStorage.getItem("isExpired") === "true" ? "/package" : "/packages";
  const couponApplied = !!appliedCouponCode;

  return (
    <form onSubmit={handleSubmit}>
      <style>{checkoutPageStyles}</style>
      <div className="pondstat-checkout-page">
        <div className="pondstat-checkout-page__container">
          <button
            type="button"
            className="pondstat-checkout-page__back"
            onClick={() => history.push(packagesPath)}
          >
            <Icon icon="solar:arrow-left-linear" width={18} />
            Back to packages
          </button>

          <div className="pondstat-checkout-summary">
            <div className="pondstat-checkout-summary__body">
              <span className="pondstat-checkout-summary__badge">Order summary</span>
              <div className="d-flex align-items-start justify-content-between gap-3">
                <div className="flex-1">
                  <div className="h5 fw-bold mb-1">{pkg.Name || "Loading..."}</div>
                  <div className="display-6 fw-bold mb-0 text-dark">
                    {couponApplied && originalTotal != null ? (
                      <>
                        <span className="text-muted text-decoration-line-through h5 me-2">
                          ${Number(originalTotal).toFixed(2)}
                        </span>
                        ${Number(discountedTotal ?? displayTotal).toFixed(2)}
                      </>
                    ) : (
                      <>${Number(displayTotal || 0).toFixed(2)}</>
                    )}
                    <small className="h6 text-body text-opacity-50 ms-1">
                      {formatDurationLabel(pkg.Duration || 12)}
                    </small>
                  </div>
                  {couponApplied && Number(discountAmount) > 0 && (
                    <div className="small text-success mt-1">
                      You save ${Number(discountAmount).toFixed(2)} on your first charge
                    </div>
                  )}
                  {pkg.AllowsExtraPonds && licensedPondCount > 1 && (
                    <div className="small text-muted mt-2">
                      {licensedPondCount} licensed pond{licensedPondCount === 1 ? "" : "s"}
                    </div>
                  )}
                </div>
                <div
                  className="d-flex align-items-center justify-content-center rounded-3"
                  style={{
                    width: 52,
                    height: 52,
                    background: "linear-gradient(135deg, rgba(78, 205, 196, 0.2), rgba(43, 122, 120, 0.1))",
                  }}
                >
                  <Icon className="text-theme" icon="solar:usb-bold-duotone" width={32} />
                </div>
              </div>
              {pkg.Description && (
                <div className="mt-3 pt-3 border-top">
                  {/<[a-z][\s\S]*>/i.test(pkg.Description) ? (
                    <div
                      className="text-muted small mb-0 package-description"
                      dangerouslySetInnerHTML={{ __html: pkg.Description }}
                    />
                  ) : (
                    <p className="text-muted small mb-0">{pkg.Description}</p>
                  )}
                </div>
              )}
              {featureList.length > 0 && (
                <ul className="small text-muted mt-3 mb-0 ps-0 list-unstyled">
                  {featureList.slice(0, 4).map((f) => (
                    <li key={f} className="d-flex align-items-center gap-2 mb-1">
                      <Icon icon="solar:check-circle-bold" className="text-success" width={16} />
                      {f}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <StripePaymentSection
            amount={amountDue}
            renewalAmount={renewalAmount ?? displayTotal}
            dueToday={dueToday}
            loading={loading}
            disabled={!clientSecret}
            error={error}
            submitLabel={
              dueToday === 0
                ? "Start 14-day free trial"
                : `Subscribe $${Number((discountedTotal ?? displayTotal) || 0).toFixed(2)}`
            }
            customerName={customerName}
            customerEmail={customerEmail}
            country={country}
            onCountryChange={setCountry}
            showCountry={false}
            recurringLabel={recurringLabel}
            onCancel={() => history.push(packagesPath)}
            cancelLabel="Choose a different plan"
            showCoupon
            couponCode={couponCode}
            onCouponCodeChange={setCouponCode}
            onApplyCoupon={handleApplyCoupon}
            couponLoading={couponLoading}
            couponMessage={couponMessage}
            couponApplied={couponApplied}
            couponError={couponError}
            originalTotal={originalTotal}
            discountAmount={discountAmount}
            discountedTotal={discountedTotal}
          />
        </div>
      </div>
    </form>
  );
};

export default Payment;
